// src/views/dashboard/AIDashboard.jsx
"use client";

import React, { useState, Suspense, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import GlassPanel from "../../components/ui/GlassPanel";
import BannerCarousel from "../../components/ui/BannerCarousel";
import Spinner from "../../components/Spinner";
import banner1 from "../../assets/banner1.png";
import banner2 from "../../assets/banner2.png";
import { useOrganizations } from "../../hooks/useOrganizations";
import MetricsService from "../../services/metrics.service";

// Importa la vista de creación (ajusta la ruta según donde la guardes)
import AssistantCreateView from "../../components/AssistantsView/AssistantsView";

/* UI básicos ----------------------------------------------------------------- */
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Layers,
  CheckCircle,
  Target,
  Zap,
  Bot,
} from "lucide-react";

/* Lazy-load de componentes pesados ------------------------------------------ */
const AIMetricsChart = React.lazy(() =>
  import("../../components/ai-metrics-chart/ai-metrics-chart").then((m) => ({
    default: m.AIMetricsChart,
  }))
);
const ModelPerformanceChart = React.lazy(() =>
  import(
    "../../components/model-performance-chart/model-performance-chart"
  ).then((m) => ({ default: m.ModelPerformanceChart }))
);
const UsageAnalyticsChart = React.lazy(() =>
  import(
    "../../components/usage-analytics-chart/usage-analytics-chart"
  ).then((m) => ({ default: m.UsageAnalyticsChart }))
);
const RecentActivities = React.lazy(() =>
  import("../../components/recent-activities/recent-activities").then((m) => ({
    default: m.RecentActivities,
  }))
);
const ModelsList = React.lazy(() =>
  import("../../components/models-list/models-list").then((m) => ({
    default: m.ModelsList,
  }))
);
const WorkflowsView = React.lazy(() =>
  import("../../components/Workflows/Workflows").then((m) => ({
    default: m.default,
  }))
);
const AssistantsView = React.lazy(() =>
  import("../../components/AssistantsView/AssistantsView")
);
const AvatarsView = React.lazy(() =>
  import("../../components/AvatarsView/AvatarsView")
);
const ProjectsView = React.lazy(() =>
  import("../../components/ProjectsView/ProjectsView")
);
const SettingsView = React.lazy(() => import("../Settings/SettingsView"));
const IntegrationsView = React.lazy(() => import("../Integrations/IntegrationsView"));
const AIDashboardHeader = React.lazy(() =>
  import("../../components/LandingHeader/LandingHeader")
);
const Sidebar = React.lazy(() => import("../../components/Sidebar/Sidebar"));

/* Loader Mejorado con Spinner ------------------------------------------------ */
const Loader = () => (
  <div className="flex flex-col items-center justify-center w-full py-12 space-y-4">
    <Spinner size="lg" color="purple" />
    <p className="text-gray-400 text-sm">Loading...</p>
  </div>
);

/* Skeleton Cards para Dashboard ----------------------------------------------- */
const SkeletonCard = () => (
  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 animate-pulse">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 bg-white/10 rounded-xl skeleton" />
      <div className="flex-1 space-y-3">
        <div className="h-4 bg-white/10 rounded w-3/4 skeleton" />
        <div className="h-3 bg-white/10 rounded w-1/2 skeleton" />
      </div>
    </div>
  </div>
);

const SkeletonGrid = ({ count = 6 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

export default function AIDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const viewParam = searchParams.get('view');
  
  // Mapear parámetros de URL a nombres internos de vista
  const mapViewParam = (param) => {
    switch(param) {
      case "Integrations":
        return "Connect";
      default:
        return param;
    }
  };
  
  const [view, setView] = useState(mapViewParam(viewParam) || "Dashboard");
  const [showCreateAssistant, setShowCreateAssistant] = useState(false);
  
  // Obtener el contexto actual (Personal o Organization)
  // const { currentContext, currentAccount } = useOrganizations(); // Comentado por ahora

  // Actualizar vista si cambia el parámetro de URL
  useEffect(() => {
    if (viewParam) {
      setView(mapViewParam(viewParam));
    }
  }, [viewParam]);

  const handleSelect = (label) => {
    // Si hace clic en "Home", redirigir a la página principal usando React Router
    if (label === "Home") {
      navigate("/");
      return;
    }
    
    // Actualizar la vista y la URL
    setView(label);
    
    // Mapear el label interno a parámetro de URL
    const viewMapping = {
      "Dashboard": "Dashboard",
      "Projects": "Projects",
      "Assistants": "Assistants",
      "Workflows": "Workflows",
      "Avatars": "Avatars",
      "Connect": "Integrations",
      "Settings": "Settings"
    };
    
    const urlView = viewMapping[label] || label;
    navigate(`/dashboard?view=${urlView}`, { replace: true });
  };

  // Si está activa la vista de creación de assistant, muestra SOLO eso
  if (showCreateAssistant) {
    return (
      <AssistantCreateView onBack={() => setShowCreateAssistant(false)} />
    );
  }

  // Determinar la vista principal
  const renderMainContent = () => {
    switch (view) {
      case "Dashboard":
        return (
          <GlassPanel className="p-4 md:p-8 max-w-screen-2xl mx-auto ml-20 lg:ml-60 mr-4">
            <HomePanel />
          </GlassPanel>
        );
      case "Projects":
        return (
          <GlassPanel className="p-4 md:p-8 max-w-screen-2xl mx-auto ml-20 lg:ml-60 mr-4">
            <Suspense fallback={<Loader />}>
              <ProjectsView />
            </Suspense>
          </GlassPanel>
        );
      case "Avatars":
        return (
          <GlassPanel className="p-4 md:p-8 max-w-screen-2xl mx-auto ml-20 lg:ml-60 mr-4">
            <Suspense fallback={<Loader />}>
              <AvatarsView />
            </Suspense>
          </GlassPanel>
        );
      case "Workflows":
        return (
          <GlassPanel className="p-4 md:p-8 max-w-screen-2xl mx-auto ml-20 lg:ml-60 mr-4">
          <Suspense fallback={<Loader />}>
            <WorkflowsView />
          </Suspense>
          </GlassPanel>
        );
      case "Assistants":
        return (
          <GlassPanel className="p-4 md:p-8 max-w-screen-2xl mx-auto ml-20 lg:ml-60 mr-4">
          <Suspense fallback={<Loader />}>
            <AssistantsView onCreateNew={() => setShowCreateAssistant(true)} />
          </Suspense>
          </GlassPanel>
        );
      case "Connect":
        return (
          <GlassPanel className="p-4 md:p-8 max-w-screen-2xl mx-auto ml-20 lg:ml-60 mr-4">
          <Suspense fallback={<Loader />}>
            <IntegrationsView />
          </Suspense>
          </GlassPanel>
        );
      case "Settings":
        return (
          <GlassPanel className="p-4 md:p-8 max-w-screen-2xl mx-auto ml-20 lg:ml-60 mr-4">
          <Suspense fallback={<Loader />}>
            <SettingsView />
          </Suspense>
          </GlassPanel>
        );
      default:
        return (
          <GlassPanel className="p-4 md:p-8 ml-20 lg:ml-60 mr-4">
            <HomePanel />
          </GlassPanel>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 relative overflow-hidden">
      {/* Capa negra de fondo */}
      <div className="absolute inset-0 bg-black" />

      {/* Halo morado */}
      <div
        className="absolute rounded-full"
        style={{
          top: "1rem",
          left: "4rem",
          width: "33rem",
          height: "33rem",
          background:
            "radial-gradient(circle at 25% 25%, rgba(75,0,130,0.8) 0%, rgba(75,0,130,0.4) 50%, transparent 90%)",
          filter: "blur(140px)",
        }}
      />

      {/* Header + Sidebar */}
      <Suspense fallback={<Loader />}>
        <AIDashboardHeader />
      </Suspense>
      <Suspense fallback={<Loader />}>
        <Sidebar active={view} onSelect={handleSelect} />
      </Suspense>

      {/* MAIN ------------------------------------------------------------- */}
      <main className="relative z-0 pt-40">
        {renderMainContent()}
      </main>
    </div>
  );
}

function HomePanel({ onCreateWorkflow }) {
  const [metricsData, setMetricsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMetrics() {
      try {
        const data = await MetricsService.getDashboardMetrics();
        setMetricsData(data);
      } catch (error) {
        console.error('Error loading metrics:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchMetrics();
  }, []);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-8 opacity-0 animate-slide-blur [animation-fill-mode:forwards]">
      {/* Banner superior */}
      <BannerCarousel
        images={[banner1, banner2]}
        interval={8000}
        showIndicators={true}
        className="w-full h-[400px] rounded-3xl shadow-xl mb-24"
      >
        {/* Overlay de texto y botón */}
        <div className="flex flex-col justify-center h-full pl-4 md:pl-12 gap-6 z-20">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white drop-shadow-lg max-w-2xl leading-tight">
            <span className="block">Have questions?</span>
            <span className="block text-white font-extrabold">Zeno Bot</span>
            <span className="block text-white/80">
              is here to assist<br />you in resolving them
            </span>
          </h2>
          <Button
            className="mt-2 w-max px-8 py-3 rounded-full bg-purple-700 hover:bg-purple-600 text-lg text-white font-semibold shadow-2xl transition"
          >
            Chat with Zeno
          </Button>
        </div>
      </BannerCarousel>

      {/* Métricas */}
      <MetricCards data={metricsData} />

      {/* Gráficas */}
      <Suspense fallback={<Loader />}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AIMetricsChart data={metricsData?.workflow_analytics} />
          <ModelPerformanceChart data={metricsData?.assistant_performance} />
        </div>
      </Suspense>

      {/* Sección inferior */}
      <Suspense fallback={<Loader />}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <UsageAnalyticsChart data={metricsData?.workflow_performance} />
          </div>
          <RecentActivities data={metricsData?.recent_activities} />
        </div>
      </Suspense>

      {/* Workflows (preview dentro de Home) */}
      <Suspense fallback={<Loader />}>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-100">
                Active Workflows
              </h2>
              <p className="text-gray-300">
                Manage and monitor your workflows and assistants
              </p>
            </div>
            <Button 
              className="button-3d text-white"
              onClick={onCreateWorkflow}
            >
              Create New Workflow
            </Button>
          </div>
          <ModelsList data={metricsData?.active_workflows} />
        </div>
      </Suspense>

      {/* Analytics extra */}
      <ResourceUsageCard data={metricsData?.resource_usage} />
    </div>
  );
}

function MetricCards({ data }) {
  const overview = data?.overview || {};
  
  const formatValue = (value) => {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value?.toString() || '0';
  };

  const formatChange = (change, pct) => {
    if (pct !== undefined) return `${pct >= 0 ? '+' : ''}${pct}%`;
    if (change !== undefined) return `${change >= 0 ? '+' : ''}${change}`;
    return '';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div className="opacity-0 animate-reveal-up [animation-fill-mode:forwards]" style={{ animationDelay: '0.1s' }}>
        <MetricCard 
          title="Active Workflows" 
          value={overview.active_workflows?.toString() || '0'} 
          sub={`${formatChange(overview.active_workflows_change)} from last month`} 
          Icon={Zap}
        />
      </div>
      <div className="opacity-0 animate-reveal-up [animation-fill-mode:forwards]" style={{ animationDelay: '0.2s' }}>
        <MetricCard 
          title="Workflow Executions" 
          value={formatValue(overview.workflow_executions)} 
          sub={`${formatChange(null, overview.workflow_executions_change_pct)} from last week`} 
          Icon={Bot}
        />
      </div>
      <div className="opacity-0 animate-reveal-up [animation-fill-mode:forwards]" style={{ animationDelay: '0.3s' }}>
        <MetricCard 
          title="Assistant Responses" 
          value={`${overview.assistant_success_rate?.toFixed(0) || 0}%`} 
          sub="Success rate" 
          Icon={CheckCircle}
        />
      </div>
      <div className="opacity-0 animate-reveal-up [animation-fill-mode:forwards]" style={{ animationDelay: '0.4s' }}>
        <MetricCard 
          title="Tasks Completed" 
          value={formatValue(overview.tasks_completed)} 
          sub="This month" 
          Icon={Target}
        />
      </div>
    </div>
  );
}

function MetricCard({ title, value, sub, Icon }) {
  return (
    <Card className="metric-card-3d backdrop-blur-sm hover:bg-gray-800/70 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-purple-500/10">
      <CardHeader className="flex items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-gray-100">{title}</CardTitle>
        <Icon className="h-4 w-4 text-purple-400 transition-transform duration-300 group-hover:scale-110" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-white">{value}</div>
        <p className="text-xs text-gray-300">{sub}</p>
      </CardContent>
    </Card>
  );
}

function ResourceUsageCard({ data }) {
  const usage = data || {};
  
  const calculatePercentage = (value, max = 1000) => {
    return Math.min(Math.round((value / max) * 100), 100);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="card-3d backdrop-blur-sm w-full col-span-full">
        <CardHeader>
          <CardTitle className="text-gray-100">Resource Usage</CardTitle>
          <CardDescription className="text-gray-300">Current system utilization</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <ResourceRow 
              label="Table Operations" 
              value={calculatePercentage(usage.table_operations || 0)} 
              Icon={ArrowDownLeft}
            />
            <ResourceRow 
              label="Workflow Executions" 
              value={calculatePercentage(usage.workflow_executions || 0)} 
              Icon={ArrowUpRight}
            />
            <ResourceRow 
              label="Assistant Calls" 
              value={calculatePercentage(usage.assistant_calls || 0)} 
              Icon={Layers}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ResourceRow({ label, value, Icon }) {
  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon className="h-4 w-4 text-purple-400" />
          <span className="text-sm text-gray-200">{label}</span>
        </div>
        <span className="text-sm font-medium text-white">{value}%</span>
      </div>
      <Progress value={value} className="h-2 bg-gray-800"/>
    </>
  );
}
